import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { GlobalService } from '../../shared-services/global.service';
import { formatStat, formatDecimalWithCommas } from '../../utils/format.utils';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { Subject, takeUntil } from 'rxjs';

const FILTER_KEY_ALIASES: Record<string, string> = {
  total_investment_range: 'investment_estimate',
  project_score: 'final_score',
};
const RANGE_FILTER_KEYS = new Set(['investment_estimate', 'final_score']);

@Component({
  selector: 'app-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule, DataErrorComponent],
  templateUrl: './horizontal-bar-chart.component.html',
  styleUrl: './horizontal-bar-chart.component.scss'
})
export class HorizontalBarChartComponent implements AfterViewInit, OnDestroy {

  @Input() data!: any;
  private myChart!: echarts.ECharts;
  private currentFilters: any = {};
  private selectedCategory: string | null = null;
  public hasError = false;
  public errorMessage = '';
  private activeGlobalServiceFilters: Record<string, unknown> = {};
  private activeGlobalFilterServiceFilters: Record<string, unknown> = {};
  private unSubscribe$: Subject<void> = new Subject();

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private globalService: GlobalService,
  ) { }

  ngAfterViewInit(): void {
    this.activeGlobalServiceFilters = this.extractGlobalServiceFilters(this.globalService.getCurrentFilters());
    this.activeGlobalFilterServiceFilters = this.extractGlobalFilterServiceFilters(
      this.globalFilterService.getCurrentFilters(),
    );

    // Render once on init because filter$ is a Subject and may not replay.
    this.loadAndRenderChart(this.mergeActiveFilters());

    this.globalFilterService.filter$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((filters) => {
        const triggerWidgetId = filters?.widgetId;
        const listenerIds = this.data?.filterListner || [];

        if (triggerWidgetId && !listenerIds.includes(triggerWidgetId)) return;

        const filterKey = this.data?.filterKey || 'category';
        const selected = filters?.requestParams?.[filterKey]?.[0] || null;
        this.selectedCategory = selected;

        this.activeGlobalFilterServiceFilters = this.extractGlobalFilterServiceFilters(filters);
        this.loadAndRenderChart(this.mergeActiveFilters());
      });

    this.globalService.filters$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((filters) => {
        this.activeGlobalServiceFilters = this.extractGlobalServiceFilters(filters);
        this.loadAndRenderChart(this.mergeActiveFilters());
      });
  }

  private loadAndRenderChart(filters?: any): void {
    this.currentFilters = filters;
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    if (this.data?.chartData) {
      const chartData = this.data.chartData;
      const isEmpty = !chartData || !Array.isArray(chartData?.category) || chartData.category.length === 0;
      if (isEmpty) {
        this.hasError = true;
        this.errorMessage = 'No data available for the selected filters.';
        this.myChart.clear();
      } else {
        this.hasError = false;
        this.renderChart(chartData);
        this.addResizeListener();
      }
      this.myChart.hideLoading();
      return;
    }

    const apiMethod = this.data?.method || 'post';
    const normalizedFilters = this.extractActiveFilters(filters);
    const useRequestPayload = !!this.data?.requestPayload;

    const request$ = useRequestPayload
      ? this.configService.customApi(
        this.data.apiUrl,
        this.buildRequestPayload(normalizedFilters),
        this.data,
        apiMethod,
      )
      : this.configService.getConfig(this.data.apiUrl, {
        ...filters,
        useQueryParams: this.data?.useQueryParams || false
      }, apiMethod);

    request$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res) => {
          const chartData = useRequestPayload
            ? this.resolveChartDataFromResponse(res)
            : res?.data;

          const isEmpty = !chartData || (Array.isArray(chartData) && chartData.length === 0);
          if (isEmpty) {
            this.hasError = true;
            this.errorMessage = 'No data available for the selected filters.';
            this.myChart.clear();
          } else {
            this.hasError = false;
            this.renderChart(chartData);
            this.addResizeListener();
          }

          this.myChart.hideLoading();
        },
        error: () => {
          this.hasError = true;
          this.errorMessage = 'No Data available for the chart.';
          this.myChart.clear();
          this.myChart.hideLoading();
        }
      });
  }

  private resolveChartDataFromResponse(response: unknown): { category: string[]; value: number[] } | null {
    const responseRoot = this.resolveResponseData(response);
    const summary = this.resolveSummaryRecord(responseRoot);

    if (Array.isArray(summary?.category) && Array.isArray(summary?.value)) {
      return { category: summary.category, value: summary.value };
    }

    if (Array.isArray(this.data?.valueMap) && this.data.valueMap.length) {
      const category = this.data.valueMap.map((item: any) => String(item?.label ?? ''));
      const value = this.data.valueMap.map((item: any) => {
        const raw = this.byPath(summary, String(item?.key ?? ''));
        const numeric = Number(raw ?? 0);
        return Number.isFinite(numeric) ? numeric : 0;
      });
      return { category, value };
    }

    return null;
  }

  private extractActiveFilters(filters: any): Record<string, unknown> {
    if (filters?.requestParams && typeof filters.requestParams === 'object') {
      return { ...filters.requestParams };
    }
    if (filters?.filter && typeof filters.filter === 'object') {
      return { ...filters.filter };
    }
    return filters && typeof filters === 'object' ? { ...filters } : {};
  }

  private extractGlobalServiceFilters(source: any): Record<string, unknown> {
    const filters = (source?.filter || {}) as Record<string, unknown>;
    return this.remapFilterKeys(this.extractActiveFilters({ filter: filters }));
  }

  private extractGlobalFilterServiceFilters(source: any): Record<string, unknown> {
    const requestParams =
      source?.requestParams && typeof source.requestParams === 'object' ? source.requestParams : {};
    const queryParams =
      source?.queryParams && typeof source.queryParams === 'object' ? source.queryParams : {};
    return this.remapFilterKeys({
      ...(requestParams as Record<string, unknown>),
      ...(queryParams as Record<string, unknown>),
    });
  }

  private mergeActiveFilters(): Record<string, unknown> {
    return {
      ...this.activeGlobalServiceFilters,
      ...this.activeGlobalFilterServiceFilters,
    };
  }

  private buildRequestPayload(filters?: Record<string, unknown>): Record<string, unknown> {
    const requestPayload = this.data?.requestPayload;
    if (!requestPayload || typeof requestPayload !== 'object') {
      return {};
    }

    const clonedPayload = JSON.parse(JSON.stringify(requestPayload)) as Record<string, unknown>;

    if (!this.data?.mergeActiveFilters || !filters || !Object.keys(filters).length) {
      return clonedPayload;
    }

    const requestData = (clonedPayload['requestData'] ?? {}) as Record<string, unknown>;
    const payloadFilter = (requestData['filter'] ?? {}) as Record<string, unknown>;

    const normalizedFilters = this.remapFilterKeys(filters);

    Object.entries(normalizedFilters).forEach(([key, rawValue]) => {
      const payloadValue = this.normalizeFilterPayloadValue(key, rawValue);
      if (payloadValue) {
        payloadFilter[key] = payloadValue;
      }
    });

    requestData['filter'] = payloadFilter;
    clonedPayload['requestData'] = requestData;
    return clonedPayload;
  }

  private resolveResponseData(response: unknown): unknown {
    const configuredPath = this.data?.responsePath;
    if (typeof configuredPath === 'string' && configuredPath.trim()) {
      const resolved = this.byPath(response, configuredPath.trim());
      if (resolved !== undefined) {
        return resolved;
      }
    }

    const roadmapCandidates = [
      'responseList.0.simulation_roadmap.0',
      'responseList.0.simulation_roadmap',
      'responseList.0.responseData.0.simulation_roadmap.0',
      'responseList.0.responseData.0.simulation_roadmap',
      'responseData.0.simulation_roadmap.0',
      'responseData.0.simulation_roadmap',
      'simulation_roadmap.0',
      'simulation_roadmap',
    ];

    for (const path of roadmapCandidates) {
      const resolved = this.byPath(response, path);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    const fallbacks = [
      'responseList.0.responseData.0',
      'responseList.0.responseData',
      'responseData.0',
      'responseData',
      'data',
    ];

    for (const path of fallbacks) {
      const resolved = this.byPath(response, path);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    return response;
  }

  private resolveSummaryRecord(source: unknown): any {
    if (Array.isArray(source)) {
      const summaryIndex = Number(this.data?.summaryIndex);
      if (Number.isInteger(summaryIndex) && summaryIndex >= 0 && summaryIndex < source.length) {
        return source[summaryIndex];
      }

      const summaryKey = typeof this.data?.summaryKey === 'string' ? this.data.summaryKey : '';
      if (summaryKey) {
        const matched = source.find(
          (item) => item && typeof item === 'object' && this.byPath(item, summaryKey) !== undefined,
        );
        if (matched) {
          return matched;
        }
      }

      return source[0];
    }
    return source;
  }

  private remapFilterKeys(filters: Record<string, unknown>): Record<string, unknown> {
    const remapped: Record<string, unknown> = {};
    Object.entries(filters || {}).forEach(([key, value]) => {
      const targetKey = FILTER_KEY_ALIASES[key] || key;
      remapped[targetKey] = value;
    });
    return remapped;
  }

  private normalizeFilterPayloadValue(key: string, value: unknown): unknown[][] | null {
    const tokens = this.normalizeFilterValues(value);
    if (!tokens.length) {
      return null;
    }

    if (RANGE_FILTER_KEYS.has(key) && tokens.length >= 2) {
      const first = Number(tokens[0]);
      const second = Number(tokens[1]);
      if (Number.isFinite(first) && Number.isFinite(second)) {
        return [[first], [second]];
      }
    }

    return [tokens];
  }

  private normalizeFilterValues(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      const normalized = value
        .flat(Infinity)
        .map((item) => (typeof item === 'string' ? item.trim() : item))
        .filter((item) => item !== null && item !== undefined && item !== '');

      const unique: unknown[] = [];
      const seen = new Set<string>();
      normalized.forEach((item) => {
        const signature = `${typeof item}:${String(item)}`;
        if (!seen.has(signature)) {
          seen.add(signature);
          unique.push(item);
        }
      });
      return unique;
    }

    if (value === null || value === undefined) {
      return [];
    }
    if (typeof value === 'string') {
      const token = value.trim();
      return token ? [token] : [];
    }
    return [value];
  }

  private byPath(source: any, path: string): any {
    return String(path)
      .split('.')
      .reduce(
        (current: any, key: string) =>
          current == null ? undefined : /^\d+$/.test(key) ? current[Number(key)] : current[key],
        source,
      );
  }

  private renderChart(chartData?: { category: string[]; value: number[] }): void {
    if (!this.data) return;

    const extendLeft = this.data.orientation === 'left';
    const roadmapStyle = this.data?.styleVariant === 'roadmap';
    const valuePrefix = this.data?.valuePrefix || '';
    const categories = [...(chartData?.category || [])];
    const values = [...(chartData?.value || [])];

    if (this.data?.reverseOrder) {
      categories.reverse();
      values.reverse();
    }

    const useGradient = !!this.data?.gradientStartColor && !!this.data?.gradientEndColor;
    const defaultBarColor = this.data.barColor || (roadmapStyle ? '#6f52ed' : '#91CC75');
    const maxValue = values.length ? Math.max(...values.map((value) => Number(value) || 0)) : 0;
    const resolvedXAxisMax =
      this.data?.xAxisMax ??
      (roadmapStyle && maxValue > 0 ? Math.ceil(maxValue * 1.13 * 10) / 10 : null);

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const data = params[0];
          const category = data?.name || '';
          const value = data?.value || 0;

          const tooltipLabels = this.data?.tooltipLabels || {};
          const categoryLabel = tooltipLabels.category || 'Category';
          const valueLabel = tooltipLabels.value || 'Value';

          const formattedValue = this.data?.useCommaStat
            ? formatDecimalWithCommas(value)
            : value;

          return `${categoryLabel}: ${category}<br/>${valueLabel}: ${valuePrefix}${formattedValue}`;
        }
      },
      grid: {
        left: roadmapStyle ? 8 : 40,
        right: roadmapStyle ? 8 : '40px',
        top: roadmapStyle ? 0 : 5,
        bottom: roadmapStyle ? 0 : 10,
        containLabel: true
      },
      xAxis: {
        type: 'value',
        inverse: extendLeft,
        max: resolvedXAxisMax,
        axisLabel: {
          show: false
        },
        splitLine: {
          show: false
        }
      },
      yAxis: {
        type: 'category',
        inverse: !!this.data?.yAxisInverse,
        name: this.data?.yAxisName || '',
        nameLocation: 'middle',
        nameGap: this.data?.yAxisName ? 50 : 0,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 14
        },
        data: categories,
        axisLabel: {
          fontSize: this.data?.yAxisLabelFontSize || (roadmapStyle ? 16 : 12),
          color: this.data?.yAxisLabelColor || '#6b7280',
          formatter: (value: string) =>
            value.length > 10 ? value.slice(0, 10) + '...' : value
        },
        splitLine: {
          show: this.data?.yAxisSplitLine?.show ?? false,
          lineStyle: {
            color: this.data?.yAxisSplitLine?.lineStyle?.color || '#d5d9e0',
            width: this.data?.yAxisSplitLine?.lineStyle?.width ?? 1,
            type: this.data?.yAxisSplitLine?.lineStyle?.type || 'solid'
          }
        }
      },
      series: [
        {
          type: 'bar',
          barWidth: this.data?.barWidth || (roadmapStyle ? 52 : undefined),
          barCategoryGap: this.data?.barCategoryGap || undefined,
          barGap: this.data?.barGap || undefined,
          showBackground: !!this.data?.showBarBackground,
          backgroundStyle: {
            color: this.data?.barBackgroundColor || '#dedede'
          },
          data: categories.map((cat, index) => {
            const value = values[index];
            const isSelected = this.selectedCategory === null || this.selectedCategory === cat;
            const fillColor = useGradient
              ? new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: this.data.gradientStartColor },
                { offset: 1, color: this.data.gradientEndColor }
              ])
              : defaultBarColor;

            return {
              value,
              itemStyle: {
                color: isSelected ? fillColor : 'rgba(200, 200, 200, 0.3)',
                shadowBlur: 0,
                shadowColor: 'transparent',
              },
              label: {
                show: true,
                offset: this.data?.labelOffset || (roadmapStyle ? [6, -12] : [2, 6]),
                position:
                  this.data?.labelPosition ||
                  (roadmapStyle ? 'insideRight' : value < 0 ? 'insideLeft' : 'insideRight'),
                color: this.data?.labelColor || (roadmapStyle ? '#111827' : 'black'),
                fontSize: this.data?.labelFontSize || (roadmapStyle ? 22 : 12),
                fontWeight: this.data?.labelFontWeight || (roadmapStyle ? 600 : 400),
                formatter: ({ value }: any) => `${valuePrefix}${formatStat(value)}`
              }
            };
          }) || [],
          emphasis: {
            label: {
              show: true
            }
          }
        }
      ]

    };
    this.myChart.setOption(option);
    setTimeout(() => this.myChart.hideLoading(), 1000);

    if (this.data?.triggerFilterEvent) {
      this.setupChartClickEvent();
    }
  }


  private setupChartClickEvent(): void {
    this.myChart.off('click');

    this.myChart.on('click', (params: any) => {
      const clickedCategory = params?.name;
      const filterKey = this.data?.filterKey || 'category';
      const widgetId = this.data?.widgetId;

      if (!clickedCategory) return;

      if (this.globalFilterService.isSelected(filterKey, clickedCategory)) {
        this.selectedCategory = null;
        this.globalFilterService.resetFilterSelection(filterKey);
      } else {
        this.selectedCategory = clickedCategory;
        this.globalFilterService.updateFilterSelection(
          filterKey,
          [clickedCategory],
          false,
          widgetId
        );
      }

      this.loadAndRenderChart(this.currentFilters);
    });
  }

  private addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
